import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { UserPermission } from 'app/modules/models/profile-management/userPermission.type';
import { isSuperAdmin } from 'app/shared/utils';
import { User_UserProfile } from 'app/modules/models/profile-management/userProfile.type';
import { StoreService } from '../../../ngrx/store.service';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
  /**
   * Constructor
   */
  permissions: UserPermission[] = [];
  constructor(
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly storeService: StoreService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl);
    return true;
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    const redirectUrl = route.path === 'sign-out' ? '/' : route.path;
    return this._check(redirectUrl);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectURL
   * @private
   */
  private _check(redirectURL: string): Observable<boolean> {
    // Check the authentication status
    return this._authService.check().pipe(
      switchMap(authenticated => {
        // If the user is not authenticated...
        if (!authenticated) {
          // Redirect to the sign-in page
          this._router.navigate(['sign-in'], { queryParams: { redirectURL } });

          // Prevent the access
          return of(false);
        }
        if (isSuperAdmin()) {
          return of(true);
        }
        const user = this.storeService.getConnectedUser();
        this.permissions = this.getPermissions(user?.user_UserProfiles || []);

        if (!user || this.permissions.length === 0) {
          this._router.navigateByUrl('/forbidden-access');
          return of(false);
        }

        const requestedRoute = this.normalizeRoute(redirectURL);
        const hasPermission = this.permissions.some(permission => {
          const permissionRoute = this.normalizeRoute(permission.pageRoute || '');
          return !!permissionRoute && (
            requestedRoute === permissionRoute ||
            requestedRoute.startsWith(permissionRoute + '/')
          );
        });

        if (!hasPermission) {
          this._router.navigateByUrl('/forbidden-access');
        }
        return of(hasPermission);
      })
    );
  }

  private getPermissions(userUserProfiles: User_UserProfile[]): UserPermission[] {
    const permissionMap = new Map<string, UserPermission>();

    (userUserProfiles || []).forEach(p => {
      if (p.userProfile?.permissions) {
        p.userProfile.permissions.forEach(permission => {
          const permissionKey = permission.pageRoute || permission.id || permission.name;
          if (!permissionMap.has(permissionKey)) {
            permissionMap.set(permissionKey, permission);
          }
        });
      }
    });

    return Array.from(permissionMap.values());
  }

  private normalizeRoute(route: string): string {
    const normalized = (route || '')
      .toLowerCase()
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');
    return normalized ? `/${normalized}` : '';
  }
}
